import React, { useState, useEffect } from 'react';
import { User, Plus, Star, TrendingUp, Palette } from 'lucide-react';
import { WizardData } from '../AIActivityWizard';
import { useChildren } from '../../hooks/useChildren';
import { Database } from '../../lib/supabase';

type Child = Database['public']['Tables']['children']['Row'];

export interface ChildSelectionStepProps {
  wizardData: WizardData;
  onStepComplete: (data: Partial<WizardData>) => void;
  onDataUpdate?: (data: Partial<WizardData>) => void;
  onNext: () => void;
  onPrevious: () => void;
  setIsGenerating: (generating: boolean) => void;
}

const avatarColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#F97316'];

const learningPreferences = [
  'Visual learner', 'Hands-on activities', 'Step-by-step instructions', 
  'Creative challenges', 'Logic puzzles', 'Story-based learning'
];

const commonStrengths = [
  'Reading comprehension', 'Mathematical reasoning', 'Creative thinking',
  'Problem-solving', 'Memory skills', 'Attention to detail'
];

const commonChallenges = [
  'Focus and attention', 'Processing speed', 'Working memory',
  'Fine motor skills', 'Social communication', 'Executive function'
];

export function ChildSelectionStep({ wizardData, onStepComplete, onDataUpdate }: ChildSelectionStepProps) {
  const { children } = useChildren();
  const [newChildForm, setNewChildForm] = useState({
    name: '',
    age: 5,
    avatar_color: avatarColors[0],
    learning_preferences: [] as string[],
    strengths: [] as string[],
    challenges: [] as string[]
  });

  // Initialize form with existing childData if available
  useEffect(() => {
    if (wizardData.childData) {
      setNewChildForm(prev => ({
        ...prev,
        ...wizardData.childData,
        learning_preferences: wizardData.childData.learning_preferences || [],
        strengths: wizardData.childData.strengths || [],
        challenges: wizardData.childData.challenges || []
      }));
    }
  }, [wizardData.childData]);

  const handleChildSelect = (child: Child) => {
    onStepComplete({ selectedChild: child });
  };

  const handleNewChildChange = (field: string, value: string | number | string[]) => {
    const updatedForm = { ...newChildForm, [field]: value };
    setNewChildForm(updatedForm);
    
    // Only update wizard data without completing the step
    // Use onDataUpdate instead of onStepComplete to avoid auto-advance
    if (onDataUpdate) {
      onDataUpdate({ childData: updatedForm });
    }
  };

  const toggleArrayItem = (array: string[], item: string, field: string) => {
    const newArray = array.includes(item)
      ? array.filter(i => i !== item)
      : [...array, item];
    handleNewChildChange(field, newArray);
  };

  if (wizardData.mode === 'existing-child') {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Select a Child
          </h3>
          <p className="text-gray-600">
            Choose which child you'd like to generate activities for
          </p>
        </div>

        {children.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-gray-400" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">No Children Found</h4>
            <p className="text-gray-600 mb-4">
              You need to create at least one child profile first.
            </p>
            <button
              onClick={() => onStepComplete({ mode: 'new-child' })}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create New Child
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {children.map((child) => {
              const level = Math.floor(child.total_points / 100) + 1;
              const isSelected = wizardData.selectedChild?.id === child.id;
              
              return (
                <div
                  key={child.id}
                  onClick={() => handleChildSelect(child)}
                  className={`p-6 rounded-xl border-2 cursor-pointer transition-all hover:shadow-lg ${
                    isSelected
                      ? 'border-purple-500 bg-purple-50 shadow-md'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-center">
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-4 shadow-md"
                      style={{ backgroundColor: child.avatar_color }}
                    >
                      {child.name.charAt(0).toUpperCase()}
                    </div>
                    
                    <h4 className="text-lg font-semibold text-gray-900 mb-1">
                      {child.name}
                    </h4>
                    <p className="text-gray-600 text-sm mb-4">
                      Age {child.age}
                    </p>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                        <Star className="w-4 h-4 text-yellow-500" />
                        <span>{child.total_points} points</span>
                      </div>
                      <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                        <TrendingUp className="w-4 h-4 text-blue-500" />
                        <span>Level {level}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Create New Child Profile
        </h3>
        <p className="text-gray-600">
          Let's set up a personalized learning profile for your child
        </p>
      </div>

      {/* Basic Information */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h4>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Child's Name
            </label>
            <input
              type="text"
              value={newChildForm.name}
              onChange={(e) => handleNewChildChange('name', e.target.value)}
              placeholder="Enter your child's name"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Age
            </label>
            <select
              value={newChildForm.age}
              onChange={(e) => handleNewChildChange('age', parseInt(e.target.value))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Array.from({ length: 13 }, (_, i) => i + 3).map(age => (
                <option key={age} value={age}>
                  {age} years old
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Avatar Color
          </label>
          <div className="flex space-x-3">
            {avatarColors.map((color) => (
              <button
                key={color}
                onClick={() => handleNewChildChange('avatar_color', color)}
                className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold border-4 transition-all ${
                  newChildForm.avatar_color === color
                    ? 'border-gray-400 scale-110'
                    : 'border-transparent hover:scale-105'
                }`}
                style={{ backgroundColor: color }}
              >
                {newChildForm.avatar_color === color && <Palette className="w-6 h-6" />}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Learning Preferences */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-2">Learning Preferences</h4>
        <p className="text-gray-600 text-sm mb-4">
          Select how your child learns best (optional)
        </p>
        
        <div className="grid md:grid-cols-2 gap-3">
          {learningPreferences.map((preference) => (
            <button
              key={preference}
              onClick={() => toggleArrayItem(newChildForm.learning_preferences, preference, 'learning_preferences')}
              className={`text-left p-3 rounded-lg border transition-colors ${
                newChildForm.learning_preferences.includes(preference)
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {preference}
            </button>
          ))}
        </div>
      </div>

      {/* Strengths & Challenges */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-2">Strengths</h4>
          <p className="text-gray-600 text-sm mb-4">
            What does your child excel at? (optional)
          </p>
          
          <div className="space-y-2">
            {commonStrengths.map((strength) => (
              <button
                key={strength}
                onClick={() => toggleArrayItem(newChildForm.strengths, strength, 'strengths')}
                className={`text-left w-full p-3 rounded-lg border text-sm transition-colors ${
                  newChildForm.strengths.includes(strength)
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {strength}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-2">Areas for Growth</h4>
          <p className="text-gray-600 text-sm mb-4">
            What areas need extra support? (optional)
          </p>
          
          <div className="space-y-2">
            {commonChallenges.map((challenge) => (
              <button
                key={challenge}
                onClick={() => toggleArrayItem(newChildForm.challenges, challenge, 'challenges')}
                className={`text-left w-full p-3 rounded-lg border text-sm transition-colors ${
                  newChildForm.challenges.includes(challenge)
                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {challenge}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Preview */}
      {newChildForm.name && (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Profile Preview</h4>
          <div className="flex items-center space-x-4">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-md"
              style={{ backgroundColor: newChildForm.avatar_color }}
            >
              {newChildForm.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h5 className="text-lg font-semibold text-gray-900">{newChildForm.name}</h5>
              <p className="text-gray-600">Age {newChildForm.age}</p>
              {newChildForm.learning_preferences.length > 0 && (
                <p className="text-sm text-blue-600 mt-1">
                  {newChildForm.learning_preferences.length} learning preference{newChildForm.learning_preferences.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}